/**
 * Route-derived breadcrumbs (refreshed shell).
 *
 * Static map from the first URL segment to a label. Dynamic id segments
 * fall through as monospace short-ids ("pi_abc…") so detail pages still
 * read sensibly without needing per-page wiring. A page can override the
 * label for its row via the optional context (future hook).
 */
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const SEGMENT_LABELS: Record<string, string> = {
  "": "Dashboard",
  tenants: "Tenants",
  operators: "Operators",
  "payment-intents": "Payment intents",
  "inbound-payments": "Inbound payments",
  "balance-queries": "Balance queries",
  "callback-inbox": "Callback inbox",
  "webhook-outbox": "Webhook outbox",
  "audit-log": "Audit log",
  new: "New",
};

function labelFor(segment: string): string {
  if (segment in SEGMENT_LABELS) return SEGMENT_LABELS[segment];
  // Likely a ULID/UUID id — render a short, monospace-friendly form.
  if (segment.length > 8) return `${segment.slice(0, 10)}…`;
  return segment;
}

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const segments = pathname.split("/").filter(Boolean);

  // Root: a single non-link "Dashboard" crumb. Skip rendering to keep
  // the topbar tidy on the home page.
  if (segments.length === 0) {
    return (
      <div className="flex items-center text-sm font-medium text-foreground">Dashboard</div>
    );
  }

  const crumbs = segments.map((segment, idx) => {
    const href = "/" + segments.slice(0, idx + 1).join("/");
    return { segment, href, label: labelFor(segment) };
  });

  return (
    <nav aria-label="Breadcrumb" className="flex items-center text-sm">
      <Link to="/" className="text-muted-foreground hover:text-foreground">
        Dashboard
      </Link>
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={c.href} className="flex items-center">
            <ChevronRight className="mx-1.5 h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            {isLast ? (
              <span
                className={cn(
                  "font-medium text-foreground",
                  !(c.segment in SEGMENT_LABELS) && c.segment.length > 8 && "font-mono text-xs",
                )}
              >
                {c.label}
              </span>
            ) : (
              <Link
                to={c.href}
                className={cn(
                  "text-muted-foreground hover:text-foreground",
                  !(c.segment in SEGMENT_LABELS) && c.segment.length > 8 && "font-mono text-xs",
                )}
              >
                {c.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
